import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { RadioCard } from 'components/Form/RadioCard/RadioCard';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { PetChip } from 'components/Pet/Chip/PetChip';
import { Alert } from 'components/UI/Alert/Alert';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useSelector } from 'react-redux';
import { getServices } from 'reducers/services/ServicesSelectors';
import { dispatch } from 'store';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { Service } from 'types/Service';
import {
    navigateToStep,
    removeOnlineBookingBookingItems,
    updateOnlineBookingBookingItem
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { getOnlineBookingEvaluationItems } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { OnlineBookingStep } from 'types/OnlineBooking';
import { getAvailableServicesForPet } from 'utils/serviceUtils';
import classes from './OnlineBookingEvaluationServiceListStep.module.scss';

export const OnlineBookingEvaluationServiceListStep: React.FC = React.memo(() => {
    const marketplace = useMarketplace();

    const services = useSelector(getServices);
    const evaluationBookingItems = useSelector(getOnlineBookingEvaluationItems);

    const [selectedBookingItemUuid, setSelectedBookingItemUuid] = React.useState<string>(
        evaluationBookingItems[0].uuid
    );

    const selectedBookingItem = React.useMemo(() => {
        return evaluationBookingItems.find(item => item.uuid === selectedBookingItemUuid);
    }, [evaluationBookingItems, selectedBookingItemUuid]);

    const submitButtonLabel = React.useMemo(() => {
        // Check if there are pets that still need services
        const petsWithoutService = evaluationBookingItems.filter(item => !item.service?.id);
        const otherPetsWithoutService = petsWithoutService.filter(
            item => item.uuid !== selectedBookingItemUuid
        );

        if (otherPetsWithoutService.length > 0) {
            return 'Next Pet';
        }

        return 'Continue';
    }, [selectedBookingItemUuid, evaluationBookingItems]);

    const isValid = React.useMemo(() => {
        const selectedItem = evaluationBookingItems.find(
            item => item.uuid === selectedBookingItemUuid
        );

        // If current selected pet has no service, invalid
        if (selectedItem && !selectedItem.service?.id) {
            return false;
        }

        // If there are pets without services, allow navigation
        if (evaluationBookingItems.some(item => !item.service?.id)) {
            return true;
        }

        // All pets have services selected
        return evaluationBookingItems.every(item => item.service !== undefined);
    }, [selectedBookingItemUuid, evaluationBookingItems]);

    const availableServices = React.useMemo(() => {
        const selectedPet = evaluationBookingItems.find(
            item => item.uuid === selectedBookingItemUuid
        )?.pet;

        if (!selectedPet || !selectedBookingItem) return [];

        // Get evaluation services for the specific business area
        const evaluationServices = getAvailableServicesForPet(
            services,
            selectedPet,
            MarketplaceServiceCategory.EVALUATION
        );

        // Filter to only show evaluation services for the correct business area
        return evaluationServices.filter(
            serviceItem =>
                serviceItem.service.evaluationMetadata?.businessAreas.includes(
                    selectedBookingItem.evaluationBusinessArea
                )
        );
    }, [services, evaluationBookingItems, selectedBookingItemUuid, selectedBookingItem]);

    const serviceChangeHandler = (service: Service) => {
        dispatch(updateOnlineBookingBookingItem(selectedBookingItemUuid, { service }));
    };

    const backHandler = React.useCallback(() => {
        if (evaluationBookingItems.some(item => !item.service && item.addons.length === 0)) {
            dispatch(
                removeOnlineBookingBookingItems(
                    evaluationBookingItems
                        .filter(item => !item.service && item.addons.length === 0)
                        .map(item => item.uuid)
                )
            );
        }

        dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
    }, [evaluationBookingItems]);

    const submitHandler = () => {
        // Check if there are pets that still need services
        const petsWithoutService = evaluationBookingItems.filter(item => !item.service?.id);
        const otherPetsWithoutService = petsWithoutService.filter(
            item => item.uuid !== selectedBookingItemUuid
        );

        if (otherPetsWithoutService.length > 0) {
            // Find the current pet index in the full list
            const currentIndex = evaluationBookingItems.findIndex(
                item => item.uuid === selectedBookingItemUuid
            );

            // Find the next pet in the full list (cycling through all pets)
            const nextIndex = (currentIndex + 1) % evaluationBookingItems.length;
            const nextPetUuid = evaluationBookingItems[nextIndex].uuid;

            // Animate the pet chip to center horizontally
            const petChip = document.getElementById(nextPetUuid);
            petChip?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

            setSelectedBookingItemUuid(nextPetUuid);
            return;
        }

        // All pets have services, proceed to date picker
        dispatch(navigateToStep(OnlineBookingStep.DatePicker));
    };

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1 gap-5">
                <div className={classes.petTabs}>
                    {evaluationBookingItems.map(item => (
                        <PetChip
                            id={item.pet.uuid}
                            key={item.pet.uuid}
                            petName={item.pet.name}
                            petType={item.pet.type}
                            checked={!!item.service?.id}
                            active={selectedBookingItemUuid === item.uuid}
                            onClick={() => setSelectedBookingItemUuid(item.uuid)}
                        />
                    ))}
                </div>

                {availableServices.length === 0 && (
                    <Alert
                        variant="warning"
                        icon={faInfoCircle}
                        text="There are no evaluation services available for your pet. Please contact the business for assistance."
                    />
                )}

                {availableServices.length > 0 && (
                    <>
                        <div className="flex flex-col gap-1">
                            <h1 className="h3 w-full">Evaluation Required</h1>
                            <p className="text-base">
                                Before booking a service, your pet(s) must first complete an
                                evaluation appointment.
                            </p>
                        </div>

                        <div className="flex flex-col gap-3">
                            {availableServices.map(item => (
                                <RadioCard
                                    key={item.service.id}
                                    title={item.service.name}
                                    description={`$${item.price.toFixed(2)}`}
                                    checked={selectedBookingItem?.service?.id === item.service.id}
                                    content={item.service.description}
                                    onChange={() => serviceChangeHandler(item.service)}
                                />
                            ))}
                        </div>
                    </>
                )}

                <div className="mt-auto hide-on-mobile-and-tablet pt-1">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        label={submitButtonLabel}
                        disabled={!isValid}
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
});

OnlineBookingEvaluationServiceListStep.displayName = 'OnlineBookingEvaluationServiceListStep';
